/**
 * Builds the collapsible section list for each report tab. Each section is
 * { key, title, badge?, body } consumed by <DisclosureGroup>. Bodies are small
 * inline renderers so the shared block components (used by PDF export / shared
 * view) stay fully-expanded and untouched.
 */
import CallToActionBlock from "./CallToActionBlock";

function Bullets({ items, ordered = false }) {
  if (!items || !items.length) return null;
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`${ordered ? "list-decimal" : "list-disc"} list-inside space-y-2 text-slate-700 leading-relaxed`}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </Tag>
  );
}

function Paras({ text }) {
  return (text || "").split("\n\n").map((p, i) => (
    <p key={i} className="text-slate-700 leading-relaxed mb-3 last:mb-0">{p}</p>
  ));
}

const RISK_SUBS = [
  ["what_is_known", "What is known"],
  ["what_is_assumed", "What is assumed"],
  ["incentive_to_underestimate", "Incentive to underestimate"],
];

function DefRows({ rows }) {
  return (
    <dl className="space-y-2">
      {rows.filter(([, v]) => v).map(([label, v]) => (
        <div key={label} className="grid grid-cols-1 sm:grid-cols-[170px_1fr] gap-1 sm:gap-3">
          <dt className="text-[11px] uppercase tracking-[0.1em] text-slate-500 pt-0.5">{label}</dt>
          <dd className="text-sm text-slate-700 leading-relaxed">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function RiskBody({ r }) {
  if (typeof r === "string") return <p className="text-slate-700 leading-relaxed">{r}</p>;
  return (
    <div className="border-l-2 border-slate-300 pl-4">
      {r.description && <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.description}</p>}
      <DefRows rows={RISK_SUBS.map(([k, label]) => [label, r[k]])} />
    </div>
  );
}

function toneFor(level = "") {
  const lvl = String(level).toUpperCase();
  if (lvl.includes("RED")) return "bg-red-500";
  if (lvl.includes("AMBER")) return "bg-amber-500";
  return "bg-teal-600";
}

function PlaybookBody({ pb }) {
  return (
    <div className="space-y-3 text-sm text-slate-700">
      {pb.trigger && <p><span className="text-slate-500">Trigger: </span>{pb.trigger}</p>}
      {Array.isArray(pb.steps) && pb.steps.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 mb-1">Steps</p>
          <ol className="list-decimal list-inside space-y-1">{pb.steps.map((st, j) => <li key={j}>{st}</li>)}</ol>
        </div>
      )}
      {Array.isArray(pb.workaround) && pb.workaround.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 mb-1">Manual workaround</p>
          <ul className="list-disc list-inside space-y-1">{pb.workaround.map((w, j) => <li key={j}>{w}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function CommBody({ tpl }) {
  return (
    <div>
      {tpl.subject && (
        <p className="text-sm mb-2"><span className="text-slate-500">Subject: </span><span className="font-medium text-slate-900">{tpl.subject}</span></p>
      )}
      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">{tpl.body}</pre>
    </div>
  );
}

function VendorTable({ items }) {
  return (
    <>
      <p className="text-sm text-slate-600 mb-3">Populate this list with each vendor's primary contact, after-hours number, escalation path, and account / SLA reference.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
              <th className="px-2 py-2 font-medium">Category</th>
              <th className="px-2 py-2 font-medium">Primary</th>
              <th className="px-2 py-2 font-medium">After-hours</th>
              <th className="px-2 py-2 font-medium">Escalation</th>
              <th className="px-2 py-2 font-medium">Account / SLA</th>
            </tr>
          </thead>
          <tbody>
            {items.map((cat, i) => (
              <tr key={i} className="border-t border-slate-200">
                <td className="px-2 py-3 font-medium text-slate-900">{cat}</td>
                <td className="px-2 py-3 text-slate-300">—</td>
                <td className="px-2 py-3 text-slate-300">—</td>
                <td className="px-2 py-3 text-slate-300">—</td>
                <td className="px-2 py-3 text-slate-300">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const arr = (x) => (Array.isArray(x) ? x : []);

export function buildTabSections(tab, s, opts = {}) {
  switch (tab) {
    case "executive": {
      const secs = [{ key: "exec_summary", title: "Executive Summary", body: <p className="text-slate-700 leading-relaxed">{s.executive_summary}</p> }];
      const downtime = s.acceptable_downtime || s.maximum_acceptable_downtime;
      if (downtime) secs.push({ key: "downtime", title: "Acceptable Downtime", body: <p className="text-slate-700 leading-relaxed">{downtime}</p> });
      if (arr(s.immediate_actions).length) secs.push({ key: "immediate", title: "Immediate Actions (Next 30 Days)", badge: `${s.immediate_actions.length}`, body: <Bullets items={s.immediate_actions} /> });
      if (arr(s.recommendations).length) secs.push({ key: "recommendations", title: "Recommendations", badge: `${s.recommendations.length}`, body: <Bullets items={s.recommendations} /> });
      secs.push({ key: "next_steps", title: "Next Steps", body: <CallToActionBlock cta={s.call_to_action} onBook={opts.onBook} bare /> });
      return secs;
    }
    case "risks": {
      const secs = arr(s.top_risks).map((r, i) => ({
        key: `risk_${i}`,
        title: typeof r === "string" ? `Risk ${i + 1}` : (r.risk || `Risk ${i + 1}`),
        badge: `Priority ${i + 1}`,
        body: <RiskBody r={r} />,
      }));
      if (arr(s.potential_oversights).length) secs.push({ key: "oversights", title: "Potential Oversights", badge: `${s.potential_oversights.length}`, body: <Bullets items={s.potential_oversights} /> });
      return secs;
    }
    case "scenarios":
      return arr(s.risk_scenarios).map((sc, i) => ({
        key: `sc_${i}`,
        title: sc.name || `Scenario ${i + 1}`,
        body: <DefRows rows={[["Impact", sc.impact], ["Controls", sc.controls], ["Gaps", sc.gaps]]} />,
      }));
    case "dependencies": {
      const secs = [];
      if (s.dependency_analysis) secs.push({ key: "dep_analysis", title: "Dependency Analysis", body: <Paras text={s.dependency_analysis} /> });
      if (arr(s.single_points_of_failure).length) secs.push({ key: "spof", title: "Single Points of Failure", badge: `${s.single_points_of_failure.length}`, body: <Bullets items={s.single_points_of_failure} /> });
      if (arr(s.continuity_strategies).length) secs.push({ key: "strategies", title: "Continuity Strategies", badge: `${s.continuity_strategies.length}`, body: <Bullets items={s.continuity_strategies} /> });
      return secs;
    }
    case "playbooks": {
      const secs = arr(s.incident_playbooks).map((pb, i) => ({
        key: `pb_${i}`,
        title: (
          <span className="inline-flex items-center gap-2">
            <span className={`inline-block h-2 w-2 rounded-full ${toneFor(pb.level)}`} />
            {pb.name || `Playbook ${i + 1}`}
          </span>
        ),
        body: <PlaybookBody pb={pb} />,
      }));
      if (arr(s.recovery_priorities).length) secs.push({ key: "recovery", title: "Recovery Priorities", badge: `${s.recovery_priorities.length}`, body: <Bullets items={s.recovery_priorities} ordered /> });
      return secs;
    }
    case "vendor": {
      const secs = [];
      if (arr(s.vendor_recovery_contacts_template).length) secs.push({ key: "vendor_template", title: "Vendor Recovery Contacts — Template", badge: `${s.vendor_recovery_contacts_template.length}`, body: <VendorTable items={s.vendor_recovery_contacts_template} /> });
      if (arr(s.vendor_recovery_contacts).length) secs.push({ key: "vendor_contacts", title: "Vendor Recovery Contacts", badge: `${s.vendor_recovery_contacts.length}`, body: <Bullets items={s.vendor_recovery_contacts} /> });
      return secs;
    }
    case "comms": {
      const secs = arr(s.customer_communication_templates).map((tpl, i) => ({
        key: `comm_${i}`,
        title: tpl.audience || `Message ${i + 1}`,
        body: <CommBody tpl={tpl} />,
      }));
      if (arr(s.customer_communication_priorities).length) secs.push({ key: "comm_priorities", title: "Customer Communication Priorities", badge: `${s.customer_communication_priorities.length}`, body: <Bullets items={s.customer_communication_priorities} /> });
      if (arr(s.staff_call_tree).length) secs.push({ key: "call_tree", title: "Staff Call Tree", badge: `${s.staff_call_tree.length}`, body: <Bullets items={s.staff_call_tree} /> });
      return secs;
    }
    default:
      return [];
  }
}
